import React, { useState } from 'react';
import { map, propEq, replace, __, reject, propSatisfies, isNil, either, isEmpty, prop, compose, head, propOr, groupBy, path, reverse, keys, pathOr, sortBy, filter, pathEq } from 'ramda';
import { CaretRightFilled, EditOutlined } from '@ant-design/icons';
import { Collapse, Timeline } from 'antd';
import { Link } from 'react-router-dom';
import { Components } from '@fasstech/flexiget';
import { mapIndexed } from 'ramda-adjunct';
import { Dot } from '..';
import SetArchiveStatusOffersMutation from '../../_graphql/mutations/offers/SetArchiveStatusOffersMutation';
import { getStatusLabel } from '../../lib/status';
import { errorMessage, successMessage } from '../../utils/messageMutation';
import ModalEditVersionComment from '../../pages/offer/editOffer/ModalEditVersionComment';
import colors from '../../colors';
import EllipsisTooltip from '../EllipsisTooltip';
import useActionGuard from '../../hooks/useActionGuard';
import VersionsTreeItem from './VersionsTreeItem';

const panelItemStyle = { borderBottom: '1px solid var(--flexi-border)' };

const VersionsTree = ({
  items,
  userCanEdit,
  editLinkPattern,
  suppressionLabel,
  archiveLabel,
  restoreLabel,
  descriptionArchive,
  nameField = 'name',
  setRefetch,
  userId,
  isOffer,
  isStyle = false
}) => {
  const [openModalEditComment, setOpenModalEditComment] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState();
  const { loading, run } = useActionGuard();

  const setArchiveStatus = (offerId, isArchived) => () => {
    const offerIdsToArchive = compose(
      map(prop('id')),
      filter(pathEq(offerId, ['version', 'itemRootId']))
    )(items);

    run(`archive-${offerId}`, (done) => SetArchiveStatusOffersMutation({ offerIds: offerIdsToArchive, isArchived: !isArchived }, done), (ok, error) => {
      if (ok && !error) {
        successMessage('offre', isArchived ? 'rétablie' : 'archivée', true);
        setRefetch(true);
      } else {
        errorMessage();
      }
    });
  };

  const onOpenModalEditComment = (version) => {
    setSelectedVersion(version);
    setOpenModalEditComment(true);
  };

  const onHideModalEditComment = () => {
    setOpenModalEditComment(false);
    setSelectedVersion();
  };

  const statusIs = (status, version) => propEq(status, 'status', version);
  const buildEditLink = replace(':itemId', __, editLinkPattern);

  items = reject(propSatisfies(isNil, 'version'))(items);

  const displayComment = userCanEdit && isOffer;

  return (
    <>
      <ModalEditVersionComment
        visible={openModalEditComment} onHide={onHideModalEditComment} versionId={prop('versionId', selectedVersion)}
        comment={prop('comment', selectedVersion)}
      />
      <Collapse
        expandIcon={({ isActive }) => (
          <CaretRightFilled
            className={`!text-[18px] relative !mr-1.5 top-0.5 ${isActive ? '!text-flexi-primary-color' : '!text-flexi-grey'}`}
            rotate={isActive ? 90 : 0}
          />
        )}
        bordered={false}
        className="versions_tree bg-white"
      >
        {map(item => {
          if (either(isNil, isEmpty)(prop('versions', item))) {
            return null;
          }

          const lastVersion = compose(
            head,
            propOr([], 'versions')
          )(item);

          const majorVersions = compose(
            groupBy(path(['version', 'major'])),
            propOr([], 'versions')
          )(item);

          const majorVersionsNumbers = compose(
            reverse,
            sortBy(Number),
            keys
          )(majorVersions);

          const isArchived = prop('isArchived', item);

          return (
            <Collapse.Panel
              key={prop('id', item)}
              // eslint-disable-next-line no-restricted-syntax
              style={panelItemStyle}
              header={(
                <VersionsTreeItem
                  name={prop(nameField, lastVersion)}
                  dates={prop('dates', lastVersion)}
                  version={prop('version', lastVersion)}
                  editLink={buildEditLink(prop('id', lastVersion))}
                  suppressionLabel={suppressionLabel}
                  archiveLabel={archiveLabel}
                  restoreLabel={restoreLabel}
                  descriptionArchive={descriptionArchive}
                  isStyle={isStyle}
                  uuid={prop('uuid', lastVersion)}
                  setArchiveStatus={setArchiveStatus(prop('id', item), isArchived)}
                  archiveLoading={propOr(false, `archive-${prop('id', item)}`, loading)}
                  isArchived={isArchived}
                  userCanEdit={userCanEdit}
                />
              )}
            >
              <Timeline mode="left">
                {map(versionNumber => {
                  const minorVersions = propOr([], versionNumber, majorVersions);
                  const sortedMinorVersions = reverse(sortBy(path(['version', 'minor']))(minorVersions));

                  return (
                    <Timeline.Item
                      key={`${item.id}-version-${versionNumber}`}
                      color="green"
                      dot={<Dot color={isArchived ? prop('flexi-error', colors) : prop('flexi-valid', colors)} />}
                    >
                      Version {versionNumber}

                      <Timeline mode="left" className="!mt-3">
                        {mapIndexed((itemVersion, index) => {
                          const major = path(['version', 'major'], itemVersion);
                          const minor = path(['version', 'minor'], itemVersion);
                          const comment = pathOr('', ['version', 'comment'], itemVersion);
                          const labelVersion = `Version ${major}.${minor}`;
                          return (
                            <Timeline.Item
                              key={`${prop('id', item)}-version-${versionNumber}-${itemVersion.id}`}
                              color={isArchived ? prop('flexi-error', colors) : index !== 0 ? prop('flexi-grey', colors) : prop('flexi-blue', colors)}
                              dot={<Dot color={isArchived ? prop('flexi-error', colors) : index !== 0 ? prop('flexi-grey', colors) : prop('flexi-blue', colors)} />}
                            >
                              <div className="w-full flex items-center">
                                {isArchived ? <p>{labelVersion}</p> : <Link className="shrink-0 !text-flexi-blue" to={buildEditLink(prop('id', itemVersion))}>{labelVersion}</Link>}
                                {!isArchived && (
                                  <span className="shrink-0">
                                    &nbsp;({getStatusLabel(path(['version', 'status'], itemVersion))})
                                  </span>
                                )}

                                <EllipsisTooltip
                                  text={comment}
                                  tooltipClassName="mx-2 grow min-w-0"
                                  textClassName="text-xs text-gray-400"
                                />
                                {displayComment && !isArchived && statusIs('draft', prop('version', itemVersion)) && (
                                  <Components.Tooltip title="Modifier le commentaire">
                                    <EditOutlined
                                      onClick={() => onOpenModalEditComment(prop('version', itemVersion))}
                                      className="hover:text-flexi-grey mr-2"
                                    />
                                  </Components.Tooltip>
                                )}
                              </div>
                            </Timeline.Item>
                          );
                        })(sortedMinorVersions)}
                      </Timeline>
                    </Timeline.Item>
                  );
                })(majorVersionsNumbers)}
              </Timeline>
            </Collapse.Panel>
          );
        })(items)}
      </Collapse>
    </>
  );
};

export default VersionsTree;
